import { DataFrame } from '@lib/mobx/dataframe';
import { api, ApiError, ApiRequestInit } from '@lib/network/api';
import { router } from '@pages/App/router';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { DAY, SECOND } from '@lib/utils/time';
import { serviceWorkerService } from '@services/ServiceWorkerService';
import { decodeJwt } from 'jose';
import { billingStore } from './Billing';
import { libraryPreview } from './Library/LibraryPreview';
import { trackingStore } from './Tracking';

type TabMessage = {
  type: 'REFRESH_ACCOUNT_DETAILS';
  payload?: any;
};

export type User = {
  libraries: string[];
  organizationId?: string;

  /**
   * Billing account this user's own credits and storage live on. Compared against
   * `Library.billingId` to spot uploads another account pays for.
   *
   * Genuinely optional, not defensively optional: unlike `Library.billingId`, this field is
   * **not** in the `required` set of `GET /user/me`'s response schema, so the backend may well
   * omit it. That matters because a missing id makes `resolveBillingOwnership` return `unknown`
   * for *every* library, silently switching the whole cross-billing feature off — and the
   * accounts most likely to lack it are the org/SSO ones that upload into other people's
   * libraries, i.e. exactly the cohort the feature exists for. Read it through
   * `billingStore.currentBillingId`, never directly: that getter carries the fallback.
   *
   * Note that `_id` below does *not* substitute for this. `_id` answers "whose library is
   * this?" against `Library.owner.userId` — a different question, and on an org account with
   * several users on one billing account it gives a different answer. See
   * `resolveBillingOwnership`.
   */
  billingId?: string;

  name?: { first?: string; last?: string; nickname?: string };

  email?: string;

  sso: {
    provider: 'google' | 'apple' | 'twitter' | (string & {});
    id: string;
  }[];

  settings: Record<string, unknown>;

  /**
   * The plan flags from `Billing`, served here too so a caller that only has `/user/me` can
   * still tell a subscriber from a free account. Optional in the response schema, and cached
   * by the backend for ~30s — `/billing/info` is the fresh source; these are the fallback
   * `resolvePlanAccess` reads when that fetch failed.
   */
  isFree?: boolean;
  isTrialing?: boolean;
  isPastDue?: boolean;

  isVerified: boolean;
  hasPassword: boolean;
  _id: string;
  createdAt: number;
};

const TOKEN_KEY = 'token';
// Treat a token as expired only once it's past `exp` by this margin, so a
// slightly-fast *client* clock doesn't drop a still-valid session a few seconds
// early. The server enforces `exp` with zero clock tolerance, so this only ever
// makes us more lenient than the server, never less.
const TOKEN_EXPIRY_SKEW_MS = 30 * SECOND;

export class UserStore extends DataFrame<User> {
  @observable
  token: string | null = null;

  @observable
  private isRefreshingToken = false;
  // Promise that indicates a refresh is in progress. If non-null, a refresh is currently happening
  // and concurrent calls should await this promise instead of starting a new refresh.
  private refreshTokenPromise: Promise<void> | null = null;

  private fetchPromise: Promise<User | undefined> | null = null;

  @computed
  get isLoggedIn() {
    return this.token !== null;
  }

  @computed
  get tokenIsValid() {
    return !!this.data;
  }

  @computed
  get tokenData() {
    if (!this.token) {
      return null;
    }
    try {
      // try to decode the JWT token
      return decodeJwt(this.token);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  @computed
  get tokenExpirationDate() {
    if (!this.tokenData) {
      return null;
    }
    return (this.tokenData.exp ?? 0) * SECOND;
  }

  @computed
  get isImpersonating() {
    return this.tokenData?.impersonatedBy;
  }

  // Decode-only expiry check (no signature verification — that's the server's
  // job). True once the token is past `exp` by the skew margin. Unparseable or
  // exp-less tokens return false so the normal request/401 path handles them.
  private static isJwtExpired(token: string): boolean {
    try {
      const exp = decodeJwt(token).exp;
      if (!exp) return false;
      return exp * SECOND <= Date.now() - TOKEN_EXPIRY_SKEW_MS;
    } catch {
      return false;
    }
  }

  constructor() {
    super({ autoFetch: 'nextTick' });

    if (location.search.includes('override_token=')) {
      const url = new URL(window.location.href);
      const token = url.searchParams.get('override_token');
      if (token) {
        console.log('Overriding token from URL:', token);
        localStorage.setItem(TOKEN_KEY, token);
      }
      url.searchParams.delete('override_token');
      location.replace(url.toString());
    }

    this.token = localStorage.getItem(TOKEN_KEY) || null;

    // An already-expired token can't be refreshed — the backend rejects expired
    // tokens with no grace window (atlas /auth/token/refresh → validateLoginToken →
    // jwt.verify enforces `exp`; refreshLoginToken validates before reissuing). So
    // drop it up front and let the app treat us as logged out, rather than letting
    // bootstrap fire a doomed token refresh that, on a transient network blip (e.g.
    // DNS not ready right after wake-from-sleep), hangs ~30s before bouncing to
    // /login — the "black page after vacationing" bug.
    if (this.token && UserStore.isJwtExpired(this.token)) {
      this.token = null;
      localStorage.removeItem(TOKEN_KEY);
    }

    this.lastResendTime = parseInt(
      localStorage.getItem('user.verification.lastResendTime') ??
        (Date.now() - 61 * SECOND).toString(),
    );
    makeObservable(this);

    this.reaction(
      () => this.token,
      (token) => {
        if (token) {
          localStorage.setItem(TOKEN_KEY, token);
        } else {
          localStorage.removeItem(TOKEN_KEY);
        }
      },
    );
  }

  async fetch() {
    if (!this.token) {
      return;
    }
    // Deduplicate concurrent /me requests (e.g. DataFrame autoFetch + another caller on load).
    if (this.fetchPromise) {
      return this.fetchPromise;
    }
    this.fetchPromise = (async () => {
      try {
        const user = await this.apiWithAuth<User>('/user/me');
        runInAction(() => {
          this.data = user;
        });
        return user;
      } finally {
        this.fetchPromise = null;
      }
    })();
    return this.fetchPromise;
  }

  async updateNickname(nickname: string) {
    const updated = await this.apiWithAuth<User>('/user/me', {
      method: 'PATCH',
      body: { name: { nickname } },
    });
    runInAction(() => {
      this.data = updated;
    });
    return updated;
  }

  async refreshToken() {
    // if we don't have a token or already refreshing, no refresh, or if we are impersonating
    if (!this.tokenExpirationDate || this.isImpersonating) {
      return;
    }

    // If a refresh is already in progress (indicated by refreshTokenPromise being non-null),
    // return the existing promise so concurrent calls share the same refresh operation
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    // Refresh if token expires within 24 hours (which includes already expired tokens)
    const expiresSoon = this.tokenExpirationDate <= Date.now() + DAY;

    // if the token still has more than a day lifetime left, no refresh
    if (!expiresSoon) {
      return;
    }

    // Create and store the refresh promise (this acts as the indicator that a refresh is in progress)
    // Concurrent calls will see this non-null promise and await it instead of starting a new refresh.
    // When this promise resolves, the token will have been updated (via api() -> setToken()),
    // so all awaiting apiWithAuth() calls will use the new token.
    this.refreshTokenPromise = (async () => {
      try {
        // Set observable flag for UI/reactivity purposes
        this.isRefreshingToken = true;

        // refresh the token - the api() function will update this.token via setToken() before resolving
        await api<unknown>('/auth/token/refresh', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
      } catch (error) {
        console.error('Error refreshing token:', error);
        // Re-throw so callers can handle the error
        throw error;
      } finally {
        // Clear the promise (indicator) and flag when done, regardless of success or failure
        // This allows future refresh attempts to proceed
        this.isRefreshingToken = false;
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  async apiWithAuth<T>(path: string, options?: ApiRequestInit) {
    // No token: same outcome as expired token – ensure logged-out state and redirect
    if (!this.token) {
      this.handleSessionExpired();
      throw new ApiError('unauthorized', 'not logged in', 401);
    }

    // Check if token is expired or about to expire
    const shouldRefresh = this.tokenExpirationDate && this.tokenExpirationDate <= Date.now() + DAY;

    let attemptedRefresh = false;

    // If token needs refresh, await it before making the API call
    if (shouldRefresh && !this.isImpersonating) {
      attemptedRefresh = true;
      try {
        await this.refreshToken();
      } catch (error) {
        // Re-check if token is expired after refresh attempt
        // The token may have been refreshed by a concurrent call during the await
        const stillExpired = this.tokenExpirationDate && this.tokenExpirationDate <= Date.now();
        if (stillExpired) {
          // Distinguish a genuine server-side auth rejection from a transient
          // connectivity failure. Only a 401 means the session is truly dead.
          // A network/DNS error or timeout (the request never reached the server —
          // e.g. right after wake-from-sleep) must NOT log the user out: doing so,
          // then bouncing to /login after a ~30s hang, is the "blank screen after
          // vacationing" bug. Keep the token and rethrow the connectivity error so
          // the bootstrap UI can show a retry; a later attempt succeeds once the
          // network is back.
          if (error instanceof ApiError && error.shouldInvalidateToken) {
            console.error('Token is expired and the server rejected the refresh:', error);
            this.handleSessionExpired();
            throw new ApiError('unauthorized', 'Token expired and cannot be refreshed', 401);
          }
          console.error('Token refresh failed on a connectivity error; keeping session:', error);
          throw error;
        }
        // Token might have been refreshed by a concurrent call or is still valid
        console.error('Error refreshing token before API call:', error);
      }
    }

    if (!options) options = {};
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${this.token}`,
    };
    try {
      return await api<T>(path, options);
    } catch (error) {
      // If we get a 401 and token might be expired, try refreshing and retry once
      if (error instanceof ApiError && error.shouldInvalidateToken) {
        // Only retry if we haven't already tried refreshing in this call
        if (!attemptedRefresh && !this.isImpersonating) {
          try {
            await this.refreshToken();
          } catch (refreshError) {
            console.error('Error refreshing token on 401:', refreshError);
            this.handleSessionExpired();
            throw error; // Re-throw original 401
          }
          // Retry the request with the new token
          options.headers = {
            ...options.headers,
            Authorization: `Bearer ${this.token}`,
          };
          try {
            return await api<T>(path, options);
          } catch (retryError) {
            // If retry also fails with 401, invalidate token
            if (retryError instanceof ApiError && retryError.shouldInvalidateToken) {
              this.handleSessionExpired();
            }
            throw retryError;
          }
        }
        this.handleSessionExpired();
      }
      throw error;
    }
  }

  /**
   * Centralized handling for "user needs to log in" – no token, or 401 from API (expired/invalid).
   * Clears auth state and redirects to login. Safe to call when already logged out.
   */
  @action
  handleSessionExpired() {
    if (this.token) {
      this.token = null;
    }
    this.data = null;
    billingStore.data = null;
    billingStore.clearBillingFromLocalStorage();
    localStorage.removeItem('libraryPreviewTokens');
    libraryPreview.deinit();
    // Only redirect if not already on login page to prevent infinite loops
    if (router.state.location.pathname !== '/login') {
      router.navigate('/login');
    }
    serviceWorkerService.postMessage({
      type: 'REFRESH_ACCOUNT_DETAILS',
    });
  }

  @action
  setToken(token: string | null) {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    this.token = token;
  }

  login = async (email: string, password: string) => {
    const user = await api<User>('/auth/email/login', {
      method: 'POST',
      body: { email, password },
    });
    runInAction(() => {
      this.data = user;
    });
    trackingStore.login('email');
    // Update billing store with user details
    await billingStore.refetchCreditsAfterOperation();
    await serviceWorkerService.postMessage({
      type: 'REFRESH_ACCOUNT_DETAILS',
    });
  };

  register = async ({
    email,
    password,
    turnstileToken,
    redirect,
  }: {
    email: string;
    password: string;
    turnstileToken: string;
    redirect?: string;
  }) => {
    await api<{ success: boolean }>('/auth/email/register', {
      method: 'POST',
      body: { email, password, turnstileToken, ...(redirect && { redirect }) },
    });
    trackingStore.signUp('email');
    // Registration succeeded; log straight in with the same credentials so the
    // user is authenticated before the caller navigates on. Awaited (not
    // fire-and-forget) and wrapped: a failure — e.g. the backend requires email
    // verification before a password login — is handled here instead of
    // surfacing as an unhandled rejection, and the caller's navigation no longer
    // races login's side effects. Registration still succeeds either way.
    try {
      await this.login(email, password);
    } catch (e) {
      console.warn(
        'Auto-login after registration failed; continuing to email verification:',
        e instanceof Error ? e.message : e,
      );
    }
    this.lastResendTime = Date.now();
  };

  requestMagicLink = async ({
    email,
    turnstileToken,
    redirect,
  }: {
    email: string;
    turnstileToken: string;
    redirect?: string;
  }) => {
    await api<{ success: boolean }>('/auth/email/magic-link/request', {
      method: 'POST',
      body: { email, turnstileToken, ...(redirect && { redirect }) },
    });
    trackingStore.magicLinkRequested({ redirect: !!redirect });
    this.lastResendTime = Date.now();
  };

  consumeMagicLink = async (code: string) => {
    const user = await api<User>('/auth/email/magic-link/consume', {
      method: 'POST',
      body: { token: code },
    });
    runInAction(() => {
      this.data = user;
    });
    trackingStore.login('magic-link');
    // Fire-and-forget the trailing side-effects so the consume promise
    // settles as soon as the user is authenticated. The MagicLinkPage uses
    // the resolved promise as its cue to navigate('/'); blocking it on
    // these awaits leaves the user stuck on "Signing you in…" if either
    // the credits refetch or the service-worker postMessage hangs (the
    // SW isn't always active in local dev). Errors are surfaced via the
    // existing api() / serviceWorker error paths.
    void billingStore.refetchCreditsAfterOperation().catch((e) => {
      console.warn('[consumeMagicLink] refetchCreditsAfterOperation failed', e);
    });
    void serviceWorkerService.postMessage({ type: 'REFRESH_ACCOUNT_DETAILS' }).catch((e) => {
      console.warn('[consumeMagicLink] REFRESH_ACCOUNT_DETAILS postMessage failed', e);
    });
  };

  logout = async ({ redirect = true }: { redirect?: boolean } = {}) => {
    trackingStore.logout();
    await this.apiWithAuth('/auth/logout', {
      method: 'POST',
    });
    this.setToken(null);
    // Clear billing store data on logout
    runInAction(() => {
      billingStore.data = null;
      userStore.data = null;
    });
    // Clear billing data from localStorage
    billingStore.clearBillingFromLocalStorage();
    localStorage.removeItem('libraryPreviewTokens');
    libraryPreview.deinit();
    await serviceWorkerService.postMessage({
      type: 'REFRESH_ACCOUNT_DETAILS',
    });
    if (redirect) {
      router.navigate('/login');
    }
  };

  requestPasswordReset = async (email: string) => {
    await api<void>('/auth/password/reset/request', {
      method: 'POST',
      body: { email },
    });
    trackingStore.passwordResetRequested();
  };

  resetPassword = async (code: string, password: string) => {
    await api<void>('/auth/password/reset', {
      method: 'POST',
      body: { token: code, password },
    });
    trackingStore.passwordResetCompleted();
    // Update billing store with user details
    await billingStore.refetchCreditsAfterOperation();
    await serviceWorkerService.postMessage({
      type: 'REFRESH_ACCOUNT_DETAILS',
    });
    router.navigate('/workspace'); // TODO: Change to "default" route when merging
  };

  // `currentPassword: null` = the user has no password yet (magic-link signup);
  // the backend only requires `newPassword`, so `oldPassword` is omitted.
  changePassword = async (currentPassword: string | null, newPassword: string) => {
    await this.apiWithAuth('/auth/password/change', {
      method: 'POST',
      body: {
        newPassword,
        ...(currentPassword !== null && { oldPassword: currentPassword }),
      },
    });
    trackingStore.passwordChanged({ hadPassword: currentPassword !== null });
    // Refresh `hasPassword` so the form flips to change-password mode. Best-
    // effort: the password *was* changed, so a failed refresh must not make
    // the caller report failure (the token stays valid either way).
    await this.fetch().catch(() => undefined);
  };

  confirmEmail = async (code: string) => {
    const user = await api<User>('/auth/email/verify', {
      method: 'POST',
      body: { token: code },
    });
    runInAction(() => {
      this.data = user;
    });
    trackingStore.emailVerified();
    // Update billing store with user details
    await billingStore.refetchCreditsAfterOperation();
    await serviceWorkerService.postMessage({
      type: 'REFRESH_ACCOUNT_DETAILS',
    });
  };

  lastResendTime = Date.now();
  setLastResendTime(time: number) {
    this.lastResendTime = time;
    localStorage.setItem('user.verification.lastResendTime', time.toString());
  }

  resendEmailVerification = async (email?: string) => {
    if (Date.now() - this.lastResendTime < 60 * SECOND) {
      return;
    }
    this.setLastResendTime(Date.now());
    await api<void>('/auth/email/verify/resend', {
      method: 'POST',
      body: { email: email ?? this.data?.email },
    });
  };
}

// singleton, as we don't need multiple instances of users
// also simplifies our life with api calls from other stores
export const userStore = new UserStore();

// @ts-ignore
window.userStore = userStore;
